import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { EpisodeSummary, TvDetails } from '@/types/streaming';
import { useWatchHistory } from '@/hooks/useWatchHistory';

interface TvStreamRef {
  type: 'tv';
  tmdbId: number;
  season: number;
  episode: number;
}

interface Props {
  streamRef: TvStreamRef;
  details: TvDetails;
  episodes: Record<number, EpisodeSummary[]>;
  loadSeason: (season: number) => Promise<void>;
  onChange: (next: TvStreamRef) => void;
}

interface LastWatched {
  season: number;
  episode: number;
}

function useClickAway(open: boolean, ref: React.RefObject<HTMLDivElement>, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent): void {
      if (!ref.current?.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, ref, onClose]);
}

export function TvEpisodeControls({
  streamRef,
  details,
  episodes,
  loadSeason,
  onChange,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { entries } = useWatchHistory();
  const seasons = useMemo(
    () => details.seasons
      .filter((s) => s.seasonNumber !== 0)
      .sort((a, b) => a.seasonNumber - b.seasonNumber),
    [details.seasons],
  );

  // Most-recently-watched episode for THIS series — used to mark a row in the
  // episode dropdown so users can resume without remembering where they were.
  const lastWatched = useMemo<LastWatched | null>(() => {
    let best: { entry: { seasonNumber: number; episodeNumber: number; lastUpdated: string }; ts: number } | null = null;
    for (const e of entries) {
      if (e.tvId !== streamRef.tmdbId) continue;
      if (e.seasonNumber == null || e.episodeNumber == null) continue;
      const ts = Date.parse(e.lastUpdated);
      if (!Number.isFinite(ts)) continue;
      if (!best || ts > best.ts) {
        best = {
          entry: { seasonNumber: e.seasonNumber, episodeNumber: e.episodeNumber, lastUpdated: e.lastUpdated },
          ts,
        };
      }
    }
    return best ? { season: best.entry.seasonNumber, episode: best.entry.episodeNumber } : null;
  }, [entries, streamRef.tmdbId]);

  // Lazy-load the current season's episodes if we don't have them yet.
  useEffect(() => {
    if (episodes[streamRef.season]) return;
    void loadSeason(streamRef.season).catch(() => { /* silent — the dropdown shows a fallback */ });
  }, [streamRef.season, episodes, loadSeason]);

  const currentSeasonEpisodes: EpisodeSummary[] | null = episodes[streamRef.season] ?? null;
  const currentSeasonMeta = seasons.find((s) => s.seasonNumber === streamRef.season);
  const expectedEpisodeCount = currentSeasonMeta?.episodeCount ?? 0;

  // Boundaries for prev/next navigation. If we don't yet know episodes for
  // an adjacent season, we still allow stepping into it (we'll load on demand
  // when the user actually navigates there via the dropdown). For prev/next
  // we conservatively disable when we'd cross a season boundary into unknown
  // territory.
  const prev = useMemo(() => stepEpisode(streamRef, seasons, episodes, -1), [streamRef, seasons, episodes]);
  const next = useMemo(() => stepEpisode(streamRef, seasons, episodes, +1), [streamRef, seasons, episodes]);

  const [openMenu, setOpenMenu] = useState<'season' | 'episode' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickAway(openMenu != null, containerRef, () => setOpenMenu(null));

  const pickSeason = useCallback(
    async (nextSeason: number): Promise<void> => {
      setOpenMenu(null);
      if (nextSeason === streamRef.season) return;
      // When jumping seasons, default to episode 1 — that's the conventional
      // re-entry point and avoids landing on an episode number that doesn't
      // exist in the target season.
      onChange({ ...streamRef, season: nextSeason, episode: 1 });
      if (!episodes[nextSeason]) {
        void loadSeason(nextSeason).catch(() => undefined);
      }
    },
    [streamRef, episodes, loadSeason, onChange],
  );

  const pickEpisode = useCallback(
    (nextEpisode: number): void => {
      setOpenMenu(null);
      if (nextEpisode === streamRef.episode) return;
      onChange({ ...streamRef, episode: nextEpisode });
    },
    [streamRef, onChange],
  );

  if (seasons.length === 0) return null;

  return (
    <div className="tv-episode-controls" data-testid="tv-episode-controls" ref={containerRef}>
      <div className="tv-episode-controls__pickers">
        <div className="tv-episode-controls__menu">
          <button
            type="button"
            className="tv-episode-controls__trigger"
            aria-haspopup="menu"
            aria-expanded={openMenu === 'season'}
            onClick={() => setOpenMenu((cur) => (cur === 'season' ? null : 'season'))}
          >
            {t('episodes.seasonColon', { number: streamRef.season })}
          </button>
          {openMenu === 'season' && (
            <div role="menu" className="tv-episode-controls__popover">
              {seasons.map((s) => (
                <button
                  key={s.seasonNumber}
                  type="button"
                  role="menuitem"
                  className={`tv-episode-controls__item${s.seasonNumber === streamRef.season ? ' is-active' : ''}`}
                  onClick={() => void pickSeason(s.seasonNumber)}
                >
                  <span>{t('episodes.seasonFallback', { number: s.seasonNumber })}</span>
                  {lastWatched?.season === s.seasonNumber && (
                    <span className="tv-episode-controls__lw" aria-label={t('episodes.lastWatched')}>•</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="tv-episode-controls__menu">
          <button
            type="button"
            className="tv-episode-controls__trigger"
            aria-haspopup="menu"
            aria-expanded={openMenu === 'episode'}
            onClick={() => setOpenMenu((cur) => (cur === 'episode' ? null : 'episode'))}
          >
            {t('episodes.episodeColon', { number: streamRef.episode })}
          </button>
          {openMenu === 'episode' && (
            <div role="menu" className="tv-episode-controls__popover">
              {renderEpisodeList(currentSeasonEpisodes, expectedEpisodeCount, streamRef, lastWatched, pickEpisode, t)}
            </div>
          )}
        </div>
      </div>

      <div className="tv-episode-controls__nav" role="group" aria-label={t('episodes.navGroup')}>
        <button
          type="button"
          className="tv-episode-controls__nav-btn"
          aria-label={t('episodes.navPrev')}
          disabled={!prev}
          onClick={() => prev && onChange(prev)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button
          type="button"
          className="tv-episode-controls__nav-btn"
          aria-label={t('episodes.navNext')}
          disabled={!next}
          onClick={() => next && onChange(next)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function renderEpisodeList(
  episodes: EpisodeSummary[] | null,
  expectedCount: number,
  streamRef: TvStreamRef,
  lastWatched: LastWatched | null,
  pick: (n: number) => void,
  t: TFunction,
): JSX.Element {
  // If episodes haven't loaded yet, render a stub list from the season's
  // declared episodeCount so users aren't blocked picking by number.
  const items = episodes
    ?? Array.from({ length: expectedCount }, (_, i) => ({
      seasonNumber: streamRef.season,
      episodeNumber: i + 1,
      name: '',
      overview: '',
      stillPath: null,
      airDate: null,
    }));

  if (items.length === 0) {
    return <div className="tv-episode-controls__empty">{t('episodes.noneAvailable')}</div>;
  }

  return (
    <>
      {items.map((ep) => {
        const isActive = ep.episodeNumber === streamRef.episode;
        const isLastWatched = lastWatched?.season === streamRef.season && lastWatched.episode === ep.episodeNumber;
        return (
          <button
            key={ep.episodeNumber}
            type="button"
            role="menuitem"
            className={`tv-episode-controls__item${isActive ? ' is-active' : ''}`}
            onClick={() => pick(ep.episodeNumber)}
          >
            <span>
              {t('episodes.episodeNumber', { number: ep.episodeNumber })}
              {ep.name ? ` — ${ep.name}` : ''}
            </span>
            {isLastWatched && (
              <span className="tv-episode-controls__lw" aria-label={t('episodes.lastWatched')}>•</span>
            )}
          </button>
        );
      })}
    </>
  );
}

function stepEpisode(
  ref: TvStreamRef,
  seasons: TvDetails['seasons'],
  episodes: Record<number, EpisodeSummary[]>,
  direction: -1 | 1,
): TvStreamRef | null {
  const currentSeason = seasons.find((s) => s.seasonNumber === ref.season);
  if (!currentSeason) return null;
  const inSeason = ref.episode + direction;
  if (inSeason >= 1 && inSeason <= currentSeason.episodeCount) {
    return { ...ref, episode: inSeason };
  }
  // Crossed the season boundary — try adjacent season.
  const adjacentSeason = direction === 1
    ? seasons.find((s) => s.seasonNumber > ref.season)
    : [...seasons].reverse().find((s) => s.seasonNumber < ref.season);
  if (!adjacentSeason || adjacentSeason.episodeCount <= 0) return null;
  // We trust the season's declared episodeCount; the dropdown still lazy-loads
  // episode metadata after the navigation.
  const targetEpisode = direction === 1 ? 1 : adjacentSeason.episodeCount;
  // Optional cross-check against loaded episodes for that season.
  const loaded = episodes[adjacentSeason.seasonNumber];
  if (loaded && !loaded.some((e) => e.episodeNumber === targetEpisode)) return null;
  return { ...ref, season: adjacentSeason.seasonNumber, episode: targetEpisode };
}
